/**
 * Auth Constants
 * Centralized constants for authentication-specific functionality
 */

// LocalStorage keys for auth data
export const AUTH_STORAGE_KEYS = {
  TOKEN: 'auth_token',
  USER: 'auth_user',
  USER_DETAILS: 'user_details',
  SELECTED_COMPANY: 'selected_company',
} as const;

/**
 * Keys that must outlive a session teardown. Signing out — or being signed out
 * by an expiry or a 401 — ends the *session*, not the browser's memory of how
 * this person signs in. Wiping these would blank the login page's "Last used"
 * hint at exactly the moment it is most useful.
 *
 * Deliberately not session data: nothing here identifies a user or grants
 * access. Never add a token, an id, or an email to this list.
 */
export const PRESERVED_STORAGE_KEYS = [
  'commander.lastSignInMethod',
  'flowbite-theme-mode',
] as const;

/**
 * Session teardown. Use instead of `localStorage.clear()`, which is a blunt
 * instrument that also destroys non-session preferences.
 */
export function clearSessionStorageKeys(): void {
  try {
    const preserved = PRESERVED_STORAGE_KEYS.map(
      (key) => [key, localStorage.getItem(key)] as const
    );
    localStorage.clear();
    for (const [key, value] of preserved) {
      if (value !== null) localStorage.setItem(key, value);
    }
  } catch {
    // Storage can be unavailable (private browsing, blocked cookies). A failed
    // clear must not stop the caller from redirecting away from the session.
  }
}

// Auth-specific error messages
export const AUTH_ERRORS = {
  LOGIN_FAILED: 'Login failed. Please try again.',
  SESSION_EXPIRED: 'Your session has expired. Please login again.',
  UNAUTHORIZED: 'UNAUTHORIZED',
  SOCIAL_LOGIN_FAILED: 'Social login failed. Please try again.',
  TWO_FACTOR_REQUIRED: 'TWO_FACTOR_AUTH_REQUIRED',
} as const;
